import * as React from "react";
import Fuse from "fuse.js";
import { Check, ChevronsUpDown, Plus, Search, Building2, User, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableCombobox - A searchable dropdown with fuzzy search and create option
 * 
 * Features:
 * - Fuzzy search tolerant to typos (powered by Fuse.js)
 * - Match highlighting to show what matched
 * - Quick create option for non-existent items
 * - Support for sublabels (city, job title, etc.)
 * 
 * @param {Object} props
 * @param {string} props.value - Currently selected value ID
 * @param {Function} props.onValueChange - Callback when value changes (receives id)
 * @param {string} props.placeholder - Placeholder text when nothing selected
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {Array} props.options - Array of {id, label, sublabel?} objects
 * @param {Function} props.onSearch - Async search function (optional)
 * @param {Function} props.onCreate - Callback to create new item (receives search query)
 * @param {string} props.createLabel - Label for create option (e.g., "Créer l'entreprise")
 * @param {string} props.emptyMessage - Message when no results found
 * @param {string} props.type - "company" or "contact" for icon
 * @param {boolean} props.disabled - Whether the combobox is disabled
 * @param {string} props.className - Additional CSS classes
 */
const SearchableCombobox = React.forwardRef(({
    value,
    onValueChange,
    placeholder = "Sélectionner...",
    searchPlaceholder = "Rechercher...",
    options = [],
    onSearch,
    onCreate,
    createLabel = "Créer",
    emptyMessage = "Aucun résultat",
    type = "company",
    disabled = false,
    className,
    "data-testid": dataTestId,
}, ref) => {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [filteredOptions, setFilteredOptions] = React.useState(options);
    const [isSearching, setIsSearching] = React.useState(false);

    // Create Fuse instance for fuzzy search
    const fuse = React.useMemo(() => {
        return new Fuse(options, {
            keys: [
                { name: "label", weight: 0.7 },
                { name: "sublabel", weight: 0.3 }
            ],
            threshold: 0.4, // 0 = exact match, 1 = match anything
            distance: 100, // How far to search for a match
            ignoreLocation: true, // Search entire string
            includeScore: true,
            includeMatches: true,
            minMatchCharLength: 2,
            // Extended search options for better fuzzy matching
            useExtendedSearch: false,
            findAllMatches: true,
        });
    }, [options]);

    // Get the selected option's label
    const selectedOption = options.find((opt) => opt.id === value);

    // Filter options based on search query using fuzzy search
    React.useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredOptions(options);
            return;
        }

        // Use Fuse.js for fuzzy searching
        const results = fuse.search(searchQuery);
        
        // Map results back to options format with match info
        const fuzzyFiltered = results.map(result => ({
            ...result.item,
            score: result.score,
            matches: result.matches,
        }));

        setFilteredOptions(fuzzyFiltered);
    }, [searchQuery, options, fuse]);

    // Handle async search if provided
    React.useEffect(() => {
        if (!onSearch || !searchQuery.trim()) return;

        const debounce = setTimeout(async () => {
            setIsSearching(true);
            try {
                await onSearch(searchQuery);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(debounce);
    }, [searchQuery, onSearch]);

    const handleSelect = (optionId) => {
        onValueChange(optionId === value ? "" : optionId);
        setOpen(false);
        setSearchQuery("");
    };

    const handleCreate = () => {
        if (onCreate) {
            onCreate(searchQuery.trim());
            setOpen(false);
            setSearchQuery("");
        }
    };

    // Highlight matched text in the label
    const highlightMatches = (text, matches, key) => {
        if (!matches || !searchQuery.trim()) return text;
        
        const match = matches.find(m => m.key === key);
        if (!match || !match.indices || match.indices.length === 0) return text;

        // Sort indices and merge overlapping
        const indices = [...match.indices].sort((a, b) => a[0] - b[0]);
        
        let result = [];
        let lastEnd = 0;

        indices.forEach(([start, end], i) => {
            // Add text before match
            if (start > lastEnd) {
                result.push(text.slice(lastEnd, start));
            }
            // Add highlighted match
            result.push(
                <mark key={i} className="bg-primary/20 text-primary rounded px-0.5">
                    {text.slice(start, end + 1)}
                </mark>
            );
            lastEnd = end + 1;
        });

        // Add remaining text
        if (lastEnd < text.length) {
            result.push(text.slice(lastEnd));
        }

        return result;
    };

    const Icon = type === "contact" ? User : Building2;

    // Check if we have an exact match (to hide create option)
    const hasExactMatch = React.useMemo(() => {
        if (!searchQuery.trim()) return false;
        const query = searchQuery.toLowerCase().trim();
        return options.some(opt => opt.label.toLowerCase() === query);
    }, [searchQuery, options]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    ref={ref}
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "w-full justify-between rounded-lg font-normal",
                        !value && "text-muted-foreground",
                        className
                    )}
                    data-testid={dataTestId}
                >
                    <div className="flex items-center gap-2 truncate">
                        <Icon className="h-4 w-4 shrink-0 opacity-50" />
                        <span className="truncate">
                            {selectedOption ? selectedOption.label : placeholder}
                        </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command shouldFilter={false}>
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder={searchPlaceholder}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="fuzzy-search-input"
                        />
                        {isSearching && <Loader2 className="h-4 w-4 animate-spin opacity-50" />}
                    </div>
                    <CommandList>
                        <CommandEmpty className="py-2 px-2 text-sm text-muted-foreground">
                            {emptyMessage}
                        </CommandEmpty>
                        <CommandGroup>
                            {filteredOptions.map((option) => (
                                <CommandItem
                                    key={option.id}
                                    value={option.id}
                                    onSelect={() => handleSelect(option.id)}
                                    className="cursor-pointer"
                                >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <Icon className="h-4 w-4 shrink-0 opacity-50" />
                                        <div className="flex flex-col min-w-0">
                                            <span className="truncate">
                                                {highlightMatches(option.label, option.matches, "label")}
                                            </span>
                                            {option.sublabel && (
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {highlightMatches(option.sublabel, option.matches, "sublabel")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {option.score !== undefined && searchQuery.trim() && (
                                        <span className="text-[10px] text-muted-foreground/50 mr-2">
                                            {Math.round((1 - option.score) * 100)}%
                                        </span>
                                    )}
                                    <Check
                                        className={cn(
                                            "ml-auto h-4 w-4 shrink-0",
                                            value === option.id ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        {onCreate && searchQuery.trim() && !hasExactMatch && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={handleCreate}
                                        className="cursor-pointer text-primary"
                                    >
                                        <Plus className="mr-2 h-4 w-4" />
                                        <span>{createLabel} "{searchQuery.trim()}"</span>
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
});

SearchableCombobox.displayName = "SearchableCombobox";

export { SearchableCombobox };
